import React, { useEffect, useRef, useState } from "react";
import { Link, useForm } from "@inertiajs/react";
import InputMask from "react-input-mask";
import moment from "moment";
import { CheckCircleIcon } from "@heroicons/react/solid";

// Keep in step with NewParticipantUidGenerator::GENDER_ABBREVIATIONS.
// The local preview uses these until the server returns the saved ID.
const GENDER_ABBREVIATIONS = {
  "Woman/Girl": "F",
  "Transgender Woman/Girl": "T",
  "Man/Boy": "M",
  "Transgender Man/Boy": "T",
  "Non-Binary person": "X",
  "Gender Non-Conforming person": "X",
  "Not Sure/Questioning": "X",
  "Gender not listed": "X",
  Other: "X",
  "Chose not to respond": "X",
};

const GENDER_OPTIONS = [
  { value: "Woman/Girl", label: "Woman or Girl" },
  { value: "Transgender Woman/Girl", label: "Transgender Woman or girl" },
  { value: "Man/Boy", label: "Man or Boy" },
  { value: "Transgender Man/Boy", label: "Transgender Man or Boy" },
  { value: "Non-Binary person", label: "Non-Binary Person" },
  {
    value: "Gender Non-Conforming person",
    label: "Gender nonconforming person",
  },
  { value: "Not Sure/Questioning", label: "Questioning / Not Sure" },
  { value: "Gender not listed", label: "Gender not listed" },
  { value: "Other", label: "Other" },
  { value: "Chose not to respond", label: "Chose not to respond" },
];

function lettersOnly(value) {
  return (value || "").replace(/[^A-Za-z]/g, "").toUpperCase();
}

function previewUid({ first_name, last_name, dob, gender_identity }) {
  const date = moment(dob, ["MM/DD/YYYY", "YYYY-MM-DD"], true);
  const first = lettersOnly(first_name);
  const last = lettersOnly(last_name);
  const gender = GENDER_ABBREVIATIONS[gender_identity];

  if (!date.isValid() || first.length < 1 || last.length < 1 || !gender) {
    return null;
  }

  return (
    date.format("YY") +
    last[0] +
    (last[1] || last[0]) +
    first[0] +
    gender +
    date.format("DD") +
    "A"
  );
}

function takenUidsFor(uid) {
  if (!uid) {
    return [];
  }

  const suffix = uid.slice(-1);
  if (suffix < "B" || suffix > "Z") {
    return [];
  }

  const base = uid.slice(0, -1);
  const taken = [];
  for (let code = "A".charCodeAt(0); code < suffix.charCodeAt(0); code += 1) {
    taken.push(base + String.fromCharCode(code));
  }
  return taken;
}

function formatDob(dob) {
  if (!dob) return "";

  const value = String(dob).trim();

  // Eloquent gives us a Carbon datetime. Inertia JSON-encodes that as
  // 1990-05-15T00:00:00.000000Z. Keep the calendar date from the string.
  // Parsing the full timestamp in local time turns UTC midnight into the
  // previous evening in US timezones, which would change the day in the ID.
  const calendarDate = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (calendarDate) {
    const [, year, month, day] = calendarDate;
    const parsed = moment(`${year}-${month}-${day}`, "YYYY-MM-DD", true);
    return parsed.isValid() ? parsed.format("MM/DD/YYYY") : "";
  }

  const parsed = moment(value, ["MM/DD/YYYY", "YYYY/MM/DD"], true);
  return parsed.isValid() ? parsed.format("MM/DD/YYYY") : "";
}

function genderLabel(value) {
  return (
    GENDER_OPTIONS.find((option) => option.value === value)?.label ||
    value ||
    "—"
  );
}

function ConfirmableField({
  label,
  error,
  isMissing,
  isEditing,
  isConfirmed,
  canConfirm,
  onEdit,
  onConfirm,
  children,
}) {
  const showConfirm = canConfirm && !(isConfirmed && !isEditing);
  const showChange = !isMissing || isConfirmed;

  return (
    <div className="flex flex-col gap-1 mt-1 text-gray-700">
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium">{label}</span>
        {isConfirmed && !isEditing && (
          <span className="flex items-center gap-2 text-xs font-medium text-green-700">
            Confirmed
            <CheckCircleIcon className="h-4 w-4 text-green-600" />
          </span>
        )}
      </div>
      {children}
      {(showConfirm || showChange) && (
        <div className="mt-2 flex flex-row items-center gap-4">
          {showConfirm && (
            <button
              type="button"
              onClick={onConfirm}
              className="bg-customBlue hover:bg-customBlue/80 cursor-pointer px-3 py-1 text-sm text-white"
            >
              Confirm
            </button>
          )}
          {showChange && (
            <button
              type="button"
              onClick={onEdit}
              className="bg-gray-100 hover:bg-gray-200 cursor-pointer px-3 py-1 text-sm text-gray-700"
            >
              Change
            </button>
          )}
        </div>
      )}
      {error && <span className="text-sm text-customRed">{error}</span>}
    </div>
  );
}

export default function UidMigrationWizard({
  participant,
  onCancel,
  onComplete,
  mode = "migrate",
}) {
  const isIntake = mode === "intake";
  const { data, setData, put, post, processing, errors } = useForm({
    first_name: participant.first_name || "",
    last_name: participant.last_name || "",
    dob: formatDob(participant.dob),
    gender_identity: participant.gender_identity || "",
  });

  const originallyMissing = {
    first_name: !participant.first_name,
    last_name: !participant.last_name,
    dob: formatDob(participant.dob) === "",
    gender_identity: !participant.gender_identity,
  };

  const [editing, setEditing] = useState({
    first_name: originallyMissing.first_name,
    last_name: originallyMissing.last_name,
    dob: originallyMissing.dob,
    gender_identity: originallyMissing.gender_identity,
  });

  const [confirmed, setConfirmed] = useState({
    first_name: false,
    last_name: false,
    dob: false,
    gender_identity: false,
  });

  const [serverUid, setServerUid] = useState(null);

  const fieldRefs = {
    first_name: useRef(null),
    last_name: useRef(null),
    dob: useRef(null),
    gender_identity: useRef(null),
  };
  const pendingFocus = useRef(null);

  useEffect(() => {
    const name = pendingFocus.current;
    if (!name || !editing[name]) {
      return;
    }

    const node = fieldRefs[name].current;
    pendingFocus.current = null;

    if (!node) {
      return;
    }

    if (name === "gender_identity") {
      const selected =
        node.querySelector("button.bg-customBlue") ||
        node.querySelector("button");
      selected?.focus();
      return;
    }

    node.focus();
    if (typeof node.select === "function") {
      node.select();
    }
  }, [editing]);

  function editField(name) {
    pendingFocus.current = name;
    setEditing((current) => ({ ...current, [name]: true }));
    setConfirmed((current) => ({ ...current, [name]: false }));
  }

  function confirmField(name) {
    setConfirmed((current) => ({ ...current, [name]: true }));
    setEditing((current) => ({ ...current, [name]: false }));
  }

  const previewSource = {
    first_name: confirmed.first_name ? data.first_name : "",
    last_name: confirmed.last_name ? data.last_name : "",
    dob: confirmed.dob ? data.dob : "",
    gender_identity: confirmed.gender_identity ? data.gender_identity : "",
  };
  const preview = previewUid(previewSource);
  const allConfirmed = Object.values(confirmed).every(Boolean);
  const takenUids = takenUidsFor(serverUid);

  useEffect(() => {
    if (!allConfirmed) {
      setServerUid(null);
      return;
    }
    let cancelled = false;
    window.axios
      .post(route("participants.previewNewUid"), {
        first_name: data.first_name,
        last_name: data.last_name,
        dob: data.dob,
        gender_identity: data.gender_identity,
        participant_id: participant.id || null,
      })
      .then((response) => {
        if (!cancelled) {
          setServerUid(response.data.uid);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setServerUid(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    allConfirmed,
    data.first_name,
    data.last_name,
    data.dob,
    data.gender_identity,
    participant.id,
  ]);

  function handleSubmit(e) {
    e.preventDefault();
    if (!allConfirmed) {
      return;
    }
    if (isIntake) {
      post("/participants/create5");
      return;
    }
    put(route("participants.storeNewUid", participant.id), {
      onSuccess: () => onComplete?.(),
    });
  }

  const inputClass = (isEditing) =>
    `h-10 w-full border border-gray-400 px-3 ${
      isEditing ? "bg-white" : "bg-gray-100"
    }`;

  return (
    <form onSubmit={handleSubmit} className="mb-6 flex flex-col gap-8">
      <p className="text-sm text-gray-600">
        {isIntake ? (
          <>
            Confirm each value. These will be saved on the new participant along
            with the generated ID.
          </>
        ) : (
          <>
            Old ID:{" "}
            <span className="font-semibold">{participant.participant_uid}</span>
            . Confirm each value. Nothing here updates the participant record
            except the new ID.
          </>
        )}
      </p>

      <ConfirmableField
        label="First name"
        error={errors.first_name}
        isMissing={originallyMissing.first_name}
        isEditing={editing.first_name}
        isConfirmed={confirmed.first_name}
        canConfirm={lettersOnly(data.first_name).length > 0}
        onEdit={() => editField("first_name")}
        onConfirm={() => confirmField("first_name")}
      >
        <input
          ref={fieldRefs.first_name}
          className={inputClass(editing.first_name)}
          value={data.first_name}
          readOnly={!editing.first_name}
          onChange={(e) => setData("first_name", e.target.value)}
        />
      </ConfirmableField>

      <ConfirmableField
        label="Last name"
        error={errors.last_name}
        isMissing={originallyMissing.last_name}
        isEditing={editing.last_name}
        isConfirmed={confirmed.last_name}
        canConfirm={lettersOnly(data.last_name).length > 0}
        onEdit={() => editField("last_name")}
        onConfirm={() => confirmField("last_name")}
      >
        <input
          ref={fieldRefs.last_name}
          className={inputClass(editing.last_name)}
          value={data.last_name}
          readOnly={!editing.last_name}
          onChange={(e) => setData("last_name", e.target.value)}
        />
      </ConfirmableField>

      <ConfirmableField
        label="Date of birth"
        error={errors.dob}
        isMissing={originallyMissing.dob}
        isEditing={editing.dob}
        isConfirmed={confirmed.dob}
        canConfirm={moment(data.dob, "MM/DD/YYYY", true).isValid()}
        onEdit={() => editField("dob")}
        onConfirm={() => confirmField("dob")}
      >
        <InputMask
          inputRef={(el) => {
            fieldRefs.dob.current = el;
          }}
          className={`${inputClass(editing.dob)} text-gray-700`}
          mask="99/99/9999"
          value={data.dob}
          disabled={!editing.dob}
          onChange={(e) => setData("dob", e.target.value)}
          placeholder="MM/DD/YYYY"
        />
      </ConfirmableField>

      <ConfirmableField
        label="Current gender identity"
        error={errors.gender_identity}
        isMissing={originallyMissing.gender_identity}
        isEditing={editing.gender_identity}
        isConfirmed={confirmed.gender_identity}
        canConfirm={Boolean(GENDER_ABBREVIATIONS[data.gender_identity])}
        onEdit={() => editField("gender_identity")}
        onConfirm={() => confirmField("gender_identity")}
      >
        {editing.gender_identity ? (
          <div
            ref={fieldRefs.gender_identity}
            className="flex w-full flex-wrap gap-2"
          >
            {GENDER_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={
                  data.gender_identity === option.value
                    ? "w-full bg-customBlue px-4 py-2 text-white md:w-auto hover:bg-customBlue/80 cursor-pointer"
                    : "w-full bg-gray-200 hover:bg-gray-300 cursor-pointer px-4 py-2 md:w-auto"
                }
                onClick={() => setData("gender_identity", option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        ) : (
          <div className="flex h-10 items-center border border-gray-400 bg-gray-100 px-3">
            {genderLabel(data.gender_identity)}
          </div>
        )}
      </ConfirmableField>

      <div className="rounded-md bg-gray-100 p-4">
        <p className="text-sm text-gray-600">Preview</p>
        <p className="text-2xl font-semibold tracking-wide text-gray-800">
          {serverUid || preview || "—"}
        </p>
        {takenUids.length > 0 ? (
          <div className="mt-3 rounded-md bg-customOrange p-3 text-sm text-white">
            <p className="font-semibold">
              This ID is already in use, so the new one will end in{" "}
              {serverUid.slice(-1)}.
            </p>
            <p className="mt-1">
              Search for {takenUids.join(" or ")} first to make sure this is not
              the same participant.
            </p>
          </div>
        ) : (
          <p className="mt-1 text-xs text-gray-500">
            Confirm each field above to build the ID. The last letter may change
            to B, C, … if this ID is already taken.
          </p>
        )}
      </div>

      <div className="flex flex-row gap-2">
        <button
          type="submit"
          disabled={processing || !allConfirmed || !preview}
          className="flex-grow bg-customBlue hover:bg-customBlue/80 cursor-pointer px-4 py-3 font-semibold text-white disabled:opacity-50"
        >
          {processing
            ? isIntake
              ? "Creating…"
              : "Saving…"
            : isIntake
              ? "Create participant"
              : "Save new ID"}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="bg-gray-100 hover:bg-gray-200 cursor-pointer px-4 py-3 text-gray-700"
          >
            {isIntake ? "Back" : "Not now"}
          </button>
        )}
      </div>
    </form>
  );
}
